import { C, cAdd, cMul, cConj, cScale } from './complex';

/**
 * Single-qubit noise channels acting on a 2x2 density matrix. Unlike the
 * unitary gates in gates.js, these are genuinely non-unitary — they model
 * real imperfections and can turn a pure state into a mixed one, which is
 * exactly why they operate on density matrices (via Kraus operators) rather
 * than state vectors.
 */

const matMul2 = (A, B) => [
  [cAdd(cMul(A[0][0], B[0][0]), cMul(A[0][1], B[1][0])), cAdd(cMul(A[0][0], B[0][1]), cMul(A[0][1], B[1][1]))],
  [cAdd(cMul(A[1][0], B[0][0]), cMul(A[1][1], B[1][0])), cAdd(cMul(A[1][0], B[0][1]), cMul(A[1][1], B[1][1]))],
];

const dagger2 = (A) => [[cConj(A[0][0]), cConj(A[1][0])], [cConj(A[0][1]), cConj(A[1][1])]];

// rho' = sum_i K_i rho K_i^dagger
const applyKrausChannel = (rho, krausOps) => {
  let result = [[C(0), C(0)], [C(0), C(0)]];
  for (const K of krausOps) {
    const term = matMul2(matMul2(K, rho), dagger2(K));
    result = [
      [cAdd(result[0][0], term[0][0]), cAdd(result[0][1], term[0][1])],
      [cAdd(result[1][0], term[1][0]), cAdd(result[1][1], term[1][1])],
    ];
  }
  return result;
};

// With probability p, flip the qubit (apply X). Capped conceptually at 50%
// in the UI — beyond that "more noise" would paradoxically un-flip it.
export const bitFlipChannel = (rho, p) => {
  const s0 = Math.sqrt(1 - p), s1 = Math.sqrt(p);
  const K0 = [[C(s0), C(0)], [C(0), C(s0)]];
  const K1 = [[C(0), C(s1)], [C(s1), C(0)]]; // sqrt(p) * X
  return applyKrausChannel(rho, [K0, K1]);
};

// With probability p, flip the relative phase (apply Z).
export const phaseFlipChannel = (rho, p) => {
  const s0 = Math.sqrt(1 - p), s1 = Math.sqrt(p);
  const K0 = [[C(s0), C(0)], [C(0), C(s0)]];
  const K1 = [[C(s1), C(0)], [C(0), C(-s1)]]; // sqrt(p) * Z
  return applyKrausChannel(rho, [K0, K1]);
};

// rho' = (1-p) rho + p * I/2 — mixes uniformly toward the fully-mixed state.
export const depolarizingChannel = (rho, p) => [
  [cAdd(cScale(rho[0][0], 1 - p), C(p / 2)), cScale(rho[0][1], 1 - p)],
  [cScale(rho[1][0], 1 - p), cAdd(cScale(rho[1][1], 1 - p), C(p / 2))],
];

// Dephasing / decoherence: off-diagonal coherences decay, populations
// (diagonal) are untouched — the textbook signature of decoherence, and
// visibly different from depolarizing (which shrinks everything equally).
export const dephasingChannel = (rho, p) => {
  const factor = 1 - p;
  return [
    [rho[0][0], cScale(rho[0][1], factor)],
    [cScale(rho[1][0], factor), rho[1][1]],
  ];
};

// Classical readout error: the qubit's quantum state is untouched, but the
// reported measurement bit is flipped with probability p — a different
// physical effect from the state-level channels above.
export const applyReadoutError = (bit, p, rng = Math.random) => (rng() < p ? 1 - bit : bit);
